<script setup>
import { ref, computed, onMounted } from 'vue';
import Swal from '../../extras/js/sweetalert/sweetalert2.min';
import { showToast } from '../utility/toastNotificationUtility';

const props = defineProps({ driverId: String });
const emit = defineEmits(['resolved']);

const summary = ref(null);
const loading = ref(true);
const loadError = ref('');
const isProcessing = ref(false);

const STATUS_LABELS = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  PASSED: 'Passed',
  FAILED: 'Failed',
  ACTION_REQUIRED: 'Action Required'
};

const STATUS_CLASSES = {
  NOT_STARTED: 'bg-secondary',
  IN_PROGRESS: 'bg-info',
  PASSED: 'bg-success',
  FAILED: 'bg-danger',
  ACTION_REQUIRED: 'bg-warning'
};

const statusLabel = computed(() => STATUS_LABELS[summary.value?.status] || 'Not Started');
const statusClass = computed(() => STATUS_CLASSES[summary.value?.status] || 'bg-secondary');

// A retry after a failed check is a fresh attempt, not an edit of the old one — say so on the button
// so an admin does not think they are correcting a mistake in place.
const initiateLabel = computed(() =>
  summary.value?.status === 'NOT_STARTED' ? 'Initiate Background Check' : 'Send a New Check Link');

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '');

// The same second gate the driver-facing page applies. Vue does not sanitise a bound href, and this
// renders an admin-typed value, so both renderers must refuse anything that is not https -- otherwise a
// future relaxation of DriverBackgroundCheckValidator reopens it here alone.
const safeCheckUrl = computed(() => {
  const url = summary.value?.checkUrl;
  return typeof url === 'string' && url.startsWith('https://') ? url : null;
});

// These endpoints use AdminUserAuthorizer, whose onUnauthorized REDIRECTS to /not-authorized — an
// entire rendered HTML page. fetch follows the 303, so response.text() is that document; echoing it
// would drop a full page of markup into a toast. Reachable for a DispatchUser admin, who can still use
// the verify-license and verify-vehicle buttons on this same page.
const errorTextFrom = async (response, fallback) => {
  if (response.status === 401 || response.status === 403) {
    return 'You do not have permission to manage background checks.';
  }
  const body = await response.text();
  if (!body || body.trimStart().startsWith('<')) return fallback;
  return body;
};

// `silent` keeps the card mounted during a post-action reload. Without it the root v-if="!loading"
// unmounts the whole card, so the spinner the admin should see disappears along with the button.
const fetchSummary = async (silent = false) => {
  loading.value = !silent;
  loadError.value = '';
  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/background-check`);
    if (!response.ok) throw new Error(await errorTextFrom(response, 'Failed to load background check'));
    summary.value = await response.json();
  } catch (error) {
    // A failed read must not render as "no check on file": an admin who believes that will send a
    // second link to a driver who already has a live one.
    loadError.value = error.message || 'Could not load this driver\'s background check.';
  } finally {
    loading.value = false;
  }
};

const post = async (path, body, successMessage) => {
  isProcessing.value = true;
  try {
    const response = await fetch(`/api/admin/driver/${props.driverId}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(await errorTextFrom(response, 'Request failed'));
    showToast({ title: 'Success', body: successMessage, type: 'success', delay: 7000 });
    await fetchSummary(true);
    emit('resolved');
  } catch (error) {
    showToast({
      title: 'Error',
      body: error.message || 'Something went wrong. Please try again.',
      type: 'error',
      delay: 7000
    });
  } finally {
    isProcessing.value = false;
  }
};

const showInitiateDialog = async () => {
  const result = await Swal.fire({
    title: initiateLabel.value,
    html: '<p class="text-start small mb-0">Paste the Certn invite link for this driver. '
      + 'We\'ll email it to them and show it on their application status page.</p>',
    input: 'url',
    inputPlaceholder: 'https://...',
    inputAttributes: { 'aria-label': 'Certn invite link' },
    showCancelButton: true,
    confirmButtonText: 'Send Link',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#3085d6',
    inputValidator: (value) => {
      if (!value || !value.trim()) return 'A background check link is required';
      if (!value.trim().startsWith('https://')) return 'The link must be a full https:// URL';
      return undefined;
    }
  });

  if (result.isConfirmed && result.value) {
    await post('initiate-background-check', { checkUrl: result.value.trim() },
      'Background check link sent to the driver.');
  }
};

// SweetAlert2 supports one native input, and this dialog needs three — hence custom html and a
// preConfirm that reads them back. First multi-field dialog in this codebase.
const showResultDialog = async () => {
  const result = await Swal.fire({
    title: 'Enter Background Check Results',
    html: `
      <div class="text-start">
        <label class="form-label fw-semibold" for="bgc-application-id">Certn Application ID</label>
        <input id="bgc-application-id" class="form-control" type="text"
               placeholder="From the Certn dashboard" autocomplete="off">
        <div class="form-text mb-3">The reference only &mdash; do not paste report details.</div>

        <label class="form-label fw-semibold d-block">Decision</label>
        <div class="form-check">
          <input class="form-check-input" type="radio" name="bgc-decision" id="bgc-approve" value="approve" checked>
          <label class="form-check-label" for="bgc-approve">Approve &mdash; the check passed</label>
        </div>
        <div class="form-check mb-3">
          <input class="form-check-input" type="radio" name="bgc-decision" id="bgc-reject" value="reject">
          <label class="form-check-label" for="bgc-reject">Reject &mdash; the check did not pass</label>
        </div>

        <label class="form-label fw-semibold" for="bgc-reason">Reason</label>
        <textarea id="bgc-reason" class="form-control" rows="3"
                  placeholder="Required when rejecting"></textarea>
        <div class="form-text">Internal record only. The driver is never sent this reason &mdash; their
          email simply asks them to contact support.</div>
      </div>`,
    showCancelButton: true,
    confirmButtonText: 'Save Result',
    cancelButtonText: 'Cancel',
    focusConfirm: false,
    preConfirm: () => {
      const popup = Swal.getPopup();
      const certnApplicationId = popup.querySelector('#bgc-application-id').value.trim();
      const approved = popup.querySelector('#bgc-reject').checked === false;
      const reason = popup.querySelector('#bgc-reason').value.trim();

      if (!certnApplicationId) {
        Swal.showValidationMessage('The Certn application ID is required');
        return false;
      }
      if (!approved && !reason) {
        Swal.showValidationMessage('A reason is required when rejecting a background check');
        return false;
      }
      return { certnApplicationId, approved, reason };
    }
  });

  if (result.isConfirmed && result.value) {
    await post('background-check-result', result.value,
      result.value.approved ? 'Background check approved.' : 'Background check rejected.');
  }
};

onMounted(fetchSummary);
</script>

<template>
  <div class="card" v-if="!loading">
    <div class="card-header bg-white">
      <h5 class="mb-0"><i class="fa-solid fa-shield me-2"></i>Background Check</h5>
    </div>
    <div class="card-body">
      <div v-if="loadError" class="alert alert-danger mb-0">
        <i class="fa fa-exclamation-triangle me-2"></i>
        {{ loadError }}
        <button class="btn btn-sm btn-outline-danger ms-2" @click="fetchSummary">Retry</button>
      </div>

      <template v-else-if="summary">
        <div class="mb-3">
          <span class="fw-semibold me-2">Status:</span>
          <span class="badge" :class="statusClass">{{ statusLabel }}</span>
        </div>

        <dl class="row mb-3" v-if="summary.invitedOn">
          <dt class="col-sm-4">Link Sent</dt>
          <dd class="col-sm-8">{{ formatDate(summary.invitedOn) }}</dd>

          <template v-if="summary.checkUrl">
            <dt class="col-sm-4">Certn Link</dt>
            <dd class="col-sm-8 text-break">
              <a v-if="safeCheckUrl" :href="safeCheckUrl" target="_blank" rel="noopener noreferrer">{{ safeCheckUrl }}</a>
              <span v-else class="text-danger">{{ summary.checkUrl }} (not a valid https link)</span>
            </dd>
          </template>

          <template v-if="summary.certnApplicationId">
            <dt class="col-sm-4">Application ID</dt>
            <dd class="col-sm-8 text-break"><code>{{ summary.certnApplicationId }}</code></dd>
          </template>

          <template v-if="summary.decidedOn">
            <dt class="col-sm-4">Decided</dt>
            <dd class="col-sm-8">{{ formatDate(summary.decidedOn) }}</dd>
          </template>

          <template v-if="summary.decisionReason">
            <dt class="col-sm-4">Reason</dt>
            <dd class="col-sm-8">
              {{ summary.decisionReason }}
              <div class="form-text">Internal only &mdash; never sent to the driver.</div>
            </dd>
          </template>
        </dl>

        <div v-if="summary.status === 'IN_PROGRESS' && !summary.canRecordResult"
             class="alert alert-warning">
          <i class="fa fa-exclamation-triangle me-2"></i>
          This driver is marked in progress but has no check on record. Send them a link to start one.
        </div>

        <div class="d-flex flex-wrap gap-2">
          <button v-if="summary.canInitiate" class="btn btn-outline-primary btn-sm"
                  @click="showInitiateDialog" :disabled="isProcessing">
            <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="fa-solid fa-paper-plane me-1"></i>
            {{ initiateLabel }}
          </button>

          <button v-if="summary.canRecordResult" class="btn btn-primary btn-sm"
                  @click="showResultDialog" :disabled="isProcessing">
            <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="fa-solid fa-clipboard-check me-1"></i>
            Enter Results
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
